import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { GuestTokenService } from './guest-token.service';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Only attach token for requests to our own API
  if (!req.url.startsWith(environment.apiURL)) {
    return next(req);
  }

  // Skip if request already has an Authorization header
  if (req.headers.has('Authorization')) {
    return next(req);
  }

  // Skip interceptor for auth endpoints that don't need the access token
  if (req.url.includes('/api/auth/refresh') || req.url.includes('/plans/public')) {
    return next(req);
  }

  const guestTokenService = inject(GuestTokenService);
  const authService = inject(AuthService);

  const addToken = (request: HttpRequest<any>, token: string) => {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`
    };

    // Global Client Context Header (Required for new API flow)
    let activeClientUuid: string | null = null;
    try {
      const clientStr = localStorage.getItem('client');
      if (clientStr) {
        const clientObj = JSON.parse(clientStr);
        activeClientUuid = clientObj?.uuid || clientObj?.client_uuid || clientObj?.id || null;
      }
    } catch (e) {
      // ignore parse error
    }

    if (activeClientUuid) {
      headers['x-client-id'] = String(activeClientUuid);
    }

    return request.clone({
      setHeaders: headers
    });
  };

  const handle401Error = (request: HttpRequest<any>, nextFn: HttpHandlerFn): Observable<any> => {
    // console.log('[Interceptor] Attempting to refresh token...');
    return authService.refreshToken().pipe(
      catchError((err) => {
        console.error(`[Interceptor] Refresh failure. Status: ${err.status}, Message: ${err.message}`, err);
        // Only clear auth on actual authentication/authorization failures from the refresh token API
        if (err.status === 401 || err.status === 403 || err.status === 400) {
          // console.warn('[Interceptor] Triggering logout due to refresh failure');
          authService.logout().subscribe({ error: () => {} });
          guestTokenService.clearAuthToken();
        }
        return throwError(() => err);
      }),
      switchMap((res: any) => {
        if (res && res.access_token) {
          // console.log('[Interceptor] Refresh successful, updating token');
          guestTokenService.setAuthToken(res.access_token);
          return nextFn(addToken(request, res.access_token));
        }
        
        // console.warn('[Interceptor] Refresh response missing access_token, triggering logout');
        authService.logout().subscribe({ error: () => {} });
        guestTokenService.clearAuthToken();
        return throwError(() => new Error('No access token returned'));
      })
    );
  };

  // Prevent multiple refresh token API calls via proactive check before sending
  if (guestTokenService.authToken && guestTokenService.isAuthTokenAboutToExpire()) {
    // console.log(`[Interceptor] Proactive refresh triggered for request: ${req.url}`);
    return handle401Error(req, next);
  }

  // Prefer authToken, fall back to guestToken
  const token = guestTokenService.authToken || guestTokenService.guestToken;

  if (token) {
    const cloned = addToken(req, token);
    return next(cloned).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && guestTokenService.authToken) {
          return handle401Error(req, next);
        }
        return throwError(() => error);
      })
    );
  }

  return next(req);
};
